import { useState } from "react";
import { Plus, Trash2, Search } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SpotifyPicker from "../SpotifyPicker";
import { inputCls, labelCls } from "./constants";

function ArtistRow({ idx, artist, onChange, onDelete }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const change = (k, v) => onChange({ [k]: v });
  return (
    <div
      data-testid={`artist-row-${idx}`}
      className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3 flex-wrap"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {artist.spotify_image ? (
          <img src={artist.spotify_image} alt="" className="h-10 w-10 rounded-full object-cover border border-white/10" />
        ) : (
          <div className="h-10 w-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-500 text-sm">
            {(artist.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <input
          data-testid={`artist-name-${idx}`}
          value={artist.name || ""}
          onChange={(e) => change("name", e.target.value)}
          placeholder={idx === 0 ? "Primary artist name" : "Featured artist name"}
          className={`${inputCls} !py-2 min-w-[160px] flex-1`}
        />
      </div>
      <Select value={artist.role || (idx === 0 ? "primary" : "featured")} onValueChange={(v) => change("role", v)}>
        <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs h-9 rounded-lg w-32" data-testid={`artist-role-${idx}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#0b1220] border-white/10 text-white">
          <SelectItem value="primary">Primary</SelectItem>
          <SelectItem value="featured">Featured</SelectItem>
        </SelectContent>
      </Select>
      <button
        type="button"
        data-testid={`artist-link-${idx}`}
        onClick={() => setPickerOpen(true)}
        className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border transition-colors ${
          artist.spotify_url
            ? "border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-200"
            : "border-white/15 text-slate-300 hover:bg-white/5"
        }`}
      >
        <Search size={12} /> {artist.spotify_url ? "Spotify linked" : "Link Spotify"}
      </button>
      <button
        type="button"
        data-testid={`artist-remove-${idx}`}
        onClick={onDelete}
        className="inline-flex items-center justify-center rounded-lg border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 text-slate-400 hover:text-red-400 p-2 transition-colors"
        aria-label="Remove"
      >
        <Trash2 size={14} />
      </button>
      {pickerOpen && (
        <SpotifyPicker
          initialQuery={artist.name}
          onClose={() => setPickerOpen(false)}
          onPick={(match) => {
            onChange({
              spotify_id: match.id || "",
              spotify_url: match.url || "",
              spotify_image: match.image || "",
              name: match.name || artist.name,
            });
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default function ArtistsEditor({ artists = [], onChange, fallbackName = "" }) {
  const set = (i, next) => {
    const copy = [...artists]; copy[i] = { ...copy[i], ...next }; onChange(copy);
  };
  const add = () => onChange([...artists, { name: "", role: "featured", spotify_url: "", spotify_id: "", spotify_image: "", apple_music_url: "" }]);
  const remove = (i) => onChange(artists.filter((_, j) => j !== i));

  return (
    <div data-testid="artists-editor">
      <div className="flex items-center justify-between mb-2">
        <label className={labelCls}>Artists</label>
        <button
          type="button"
          data-testid="add-artist-btn"
          onClick={add}
          className="inline-flex items-center gap-1.5 text-[11px] text-blue-400 hover:text-blue-300"
        >
          <Plus size={13} /> Add artist
        </button>
      </div>
      <div className="space-y-2">
        {artists.length === 0 && (
          <div className="text-xs text-slate-500 border border-dashed border-white/10 rounded-xl p-3 text-center">
            No artists yet — tap <span className="text-white">Add artist</span> to credit the primary artist{fallbackName ? ` (default: ${fallbackName})` : ""}.
          </div>
        )}
        {artists.map((a, i) => (
          <ArtistRow key={i} idx={i} artist={a} onChange={(n) => set(i, n)} onDelete={() => remove(i)} />
        ))}
      </div>
    </div>
  );
}
